import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import paymentService from '../../services/paymentService';
import { useAuth } from '../../contexts/AuthContext';
import { generateTabletopByCategory } from '../../services/api';
import { getAccessToken } from '../../utils/authToken';
import { exerciseTypeService, DEFAULT_EXERCISE_TYPES } from '../../services/exerciseTypeService';
import { usePartnerTokenTiers } from '../../contexts/PartnerTokenTiersContext';
import type { ExerciseType } from '../../types/exerciseType';
import type {
  ScenarioCategory,
  ScenarioSubcategory,
  BusinessContext,
} from '../../types/tabletopExercise';

const SUBCATEGORY_TO_CATEGORY: Record<string, ScenarioCategory> = {
  ransomware: 'cybersecurity',
  phishing: 'cybersecurity',
  business_email_compromise: 'cybersecurity',
  data_breach: 'cybersecurity',
  insider_threat: 'cybersecurity',
  ddos: 'cybersecurity',
  supply_chain: 'cybersecurity',
  zero_day: 'cybersecurity',
  cloud_outage: 'business_continuity',
  severe_weather: 'business_continuity',
  key_personnel: 'business_continuity',
  active_shooter: 'business_continuity',
  cyber_physical: 'converged_security',
};

const TEMPLATES = [
  { id: 'ransomware', name: 'Ransomware Attack Response', desc: 'Test response to a ransomware attack encrypting critical systems', type: 'ransomware', duration: 180, level: 'advanced', icon: '🔒', color: '#E8453C' },
  { id: 'data_breach', name: 'Data Breach Incident', desc: 'Test response to sensitive data exfiltration', type: 'data_breach', duration: 180, level: 'advanced', icon: '🛡️', color: '#F59E0B' },
  { id: 'cloud_outage', name: 'Cloud Service Outage', desc: 'Test business continuity during cloud provider outage', type: 'cloud_outage', duration: 150, level: 'intermediate', icon: '☁️', color: '#3B82F6' },
  { id: 'insider_threat', name: 'Insider Threat Detection', desc: 'Test response to internal data theft', type: 'insider_threat', duration: 150, level: 'advanced', icon: '👁️', color: '#8B5CF6' },
  { id: 'active_shooter', name: 'Active Shooter Response', desc: 'Test emergency response to active shooter incident', type: 'active_shooter', duration: 120, level: 'advanced', icon: '🚨', color: '#DC2626' },
  { id: 'phishing', name: 'Phishing Attack', desc: 'Test awareness and containment of a credential or malware phishing breach (not payment diversion)', type: 'phishing', duration: 120, level: 'intermediate', icon: '📧', color: '#10B981' },
  { id: 'business_email_compromise', name: 'Business Email Compromise', desc: 'Test response to executive or vendor email impersonation used to divert payments or steal funds', type: 'business_email_compromise', duration: 150, level: 'intermediate', icon: '✉️', color: '#0EA5E9' },
  { id: 'ddos', name: 'DDoS Attack', desc: 'Test network resilience and response to flooding', type: 'ddos', duration: 120, level: 'intermediate', icon: '🌐', color: '#6366F1' },
  { id: 'supply_chain', name: 'Supply Chain Attack', desc: 'Test vendor risk management and response', type: 'supply_chain', duration: 180, level: 'advanced', icon: '🔗', color: '#EC4899' },
];

const BASE_EXERCISE_TYPES = ['fire', 'earthquake', 'cyber', 'pandemic', 'power_outage', 'flood', 'other'];

type ExerciseLeadFields = { name: string; role: string; email: string; phone: string };

const EMPTY_EXERCISE_LEAD: ExerciseLeadFields = { name: '', role: '', email: '', phone: '' };

function buildExerciseLeadPayload(lead: ExerciseLeadFields) {
  if (!lead.name.trim() && !lead.role && !lead.email && !lead.phone) return [];
  return [{
    name: lead.name.trim(),
    role: lead.role,
    email: lead.email,
    phone: lead.phone,
  }];
}

function exerciseLeadFromParticipants(participants: unknown): ExerciseLeadFields {
  if (!Array.isArray(participants) || participants.length === 0) return { ...EMPTY_EXERCISE_LEAD };
  const row = participants[0];
  if (!row || typeof row !== 'object') return { ...EMPTY_EXERCISE_LEAD };
  const p = row as Record<string, string>;
  return {
    name: p.name ?? '',
    role: p.role ?? '',
    email: p.email ?? '',
    phone: p.phone ?? '',
  };
}

const ROLES = [
  'CEO', 'COO', 'CTO', 'CFO', 'IT Manager', 'Operations Manager', 'HR Manager', 'Security Manager',
  'Facilities Manager', 'Department Head', 'Team Lead', 'Employee', 'External Consultant', 'Other',
  'Incident Commander', 'Communications Lead', 'Technical Lead', 'Operations Lead', 'Observer',
];

const TABLETOP_ASSESSMENT_DISCLAIMER =
  'Flash AI conducts independent operational resilience assessments and ISO 22301-aligned reviews of business continuity programs and tabletop exercises. These assessments are designed to identify strengths, gaps, and opportunities for improvement. They are not accredited ISO certification audits and do not result in ISO certification.';

interface BCP {
  id: number;
  business_name: string;
  business_type?: string;
  industry?: string;
}

interface ApiTemplate {
  id: number;
  name: string;
  exercise_type: string;
  description: string;
  scenario_template: string;
  duration_minutes?: number;
  difficulty_level?: string;
  objectives?: string[];
  participants_required?: string[];
}

interface DisplayTemplate {
  id: string;
  name: string;
  desc: string;
  type: string;
  duration: number;
  level: string;
  icon: string;
  color: string;
  isApi?: boolean;
  scenarioTemplate?: string;
}

type ScenarioMode = 'ai' | 'manual' | null;

function getIconColorForType(type: string): { icon: string } {
  const t = TEMPLATES.find((x) => x.type === type);
  return { icon: t ? t.icon : '📋' };
}

function Badge({ children, variant = 'default' }: { children: React.ReactNode; variant?: string }) {
  const classMap: Record<string, string> = {
    default: 'bg-gray-100 text-gray-700',
    advanced: 'bg-red-100 text-red-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    beginner: 'bg-green-100 text-green-800',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${classMap[variant] || classMap.default}`}>
      {children}
    </span>
  );
}

function StepIndicator({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="flex items-center gap-0 mb-10">
      {steps.map((stepName, i) => {
        const isActive = i === current;
        const isDone = i < current;
        return (
          <div key={i} className="flex items-center">
            <div className="flex items-center gap-2.5">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  isDone || isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
                } ${isActive ? 'ring-4 ring-blue-100' : ''}`}
              >
                {isDone ? '✓' : i + 1}
              </div>
              <span
                className={`text-sm transition-colors ${
                  isActive ? 'font-bold text-gray-900' : isDone ? 'font-medium text-gray-900' : 'font-medium text-gray-400'
                }`}
              >
                {stepName}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-12 h-0.5 mx-3 rounded ${isDone ? 'bg-blue-600' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Dropdown sentinel value for the "No plan" option (vs. an existing BCP id)
const NO_PLAN_OPTION = '__no_plan__';

// SessionStorage key for resuming a tabletop create after Stripe checkout
const PENDING_CREATE_KEY = 'flashai_pending_tabletop_create_v1';

const CreateExerciseForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isPartnerClient = Boolean(user?.insurer_org_id && user?.role === 'USER');
  const isEditing = Boolean(id);

  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<ScenarioMode>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<DisplayTemplate | null>(null);
  const [withoutBcp, setWithoutBcp] = useState(false);
  const [businessContext, setBusinessContext] = useState<BusinessContext>({
    business_name: '',
    industry: '',
    business_type: '',
    employee_count: undefined,
    key_systems: [],
    critical_functions: [],
  });
  const [keySystemsRaw, setKeySystemsRaw] = useState('');
  const [criticalFunctionsRaw, setCriticalFunctionsRaw] = useState('');
  const [credits, setCredits] = useState<number>(0);
  const [creditsPerPack, setCreditsPerPack] = useState<number>(1);
  const [creditsLoaded, setCreditsLoaded] = useState<boolean>(false);
  const [hasSubAccess, setHasSubAccess] = useState<boolean>(false);
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState<boolean>(false);
  const [entitlementLoaded, setEntitlementLoaded] = useState(false);
  const [entitlementAllowed, setEntitlementAllowed] = useState(false);
  const { tokenTitles } = usePartnerTokenTiers();
  const [partnerCreditName, setPartnerCreditName] = useState<string | null>(null);
  const [partnerTokenTierName, setPartnerTokenTierName] = useState<string | null>(null);
  const [form, setForm] = useState({
    bcpPlan: '',
    name: '',
    type: '',
    description: '',
    date: '',
    exerciseLead: { ...EMPTY_EXERCISE_LEAD },
  });
  const [animateIn, setAnimateIn] = useState(true);
  const [bcps, setBcps] = useState<BCP[]>([]);
  const [apiTemplates, setApiTemplates] = useState<ApiTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const steps = isEditing ? ['Configure', 'Exercise Lead'] : ['Scenario Mode', 'Configure', 'Exercise Lead'];
  const currentStep = step;

  const templateOptions: DisplayTemplate[] =
    apiTemplates.length > 0
      ? apiTemplates.map((t) => {
          const { icon } = getIconColorForType(t.exercise_type);
          return {
            id: `api-${t.id}`,
            name: t.name,
            desc: t.description || t.scenario_template?.slice(0, 120) || '',
            type: t.exercise_type,
            duration: t.duration_minutes ?? 120,
            level: t.difficulty_level || 'intermediate',
            icon,
            color: '',
            isApi: true,
            scenarioTemplate: t.scenario_template,
          };
        })
      : TEMPLATES.map((t) => ({
          id: t.id,
          name: t.name,
          desc: t.desc,
          type: t.type,
          duration: t.duration,
          level: t.level,
          icon: t.icon,
          color: t.color,
          isApi: false,
          scenarioTemplate: t.desc,
        }));

  const [availableExerciseTypes, setAvailableExerciseTypes] = useState<ExerciseType[]>(DEFAULT_EXERCISE_TYPES);

  const exerciseTypeOptions = useMemo(() => {
    const dynamicKeys = availableExerciseTypes.filter((t) => t.is_active).map((t) => t.key);
    return Array.from(
      new Set([
        ...dynamicKeys,
        ...BASE_EXERCISE_TYPES,
        ...templateOptions.map((t) => t.type),
        ...TEMPLATES.map((t) => t.type),
      ])
    ).filter(Boolean);
  }, [availableExerciseTypes, templateOptions]);

  useEffect(() => {
    exerciseTypeService.getExerciseTypes().then(setAvailableExerciseTypes).catch(() => {});
  }, []);

  useEffect(() => {
    const token = getAccessToken();
    const subscriptionFetch = isPartnerClient
      ? Promise.resolve([])
      : fetch(`${API_BASE_URL}/v1/payment/subscriptions`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
          .then((r) => (r.ok ? r.json() : []))
          .catch(() => []);
    Promise.all([
      fetch(`${API_BASE_URL}/v1/bcp/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }).then((r) => (r.ok ? r.json() : [])),
      fetch(`${API_BASE_URL}/v1/tabletop-exercises/templates/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }).then((r) => (r.ok ? r.json() : [])),
      paymentService
        .getStandaloneTabletopCredits()
        .catch(() => ({ credits_remaining: 0, purchases: [], credits_per_pack: 1 })),
      subscriptionFetch,
    ])
      .then(async ([bcpData, templateData, creditsData, subs]) => {
        setBcps(Array.isArray(bcpData) ? bcpData : []);
        setApiTemplates(Array.isArray(templateData) ? templateData : []);
        setCredits(creditsData?.credits_remaining ?? 0);
        setCreditsPerPack(creditsData?.credits_per_pack ?? 1);
        setCreditsLoaded(true);
        const subArr = Array.isArray(subs) ? subs : [];
        const active = subArr.find((s: any) => s.status === 'active');
        setHasSubAccess(Boolean(active && (active.plan_type === 'premium' || (active.addons || []).some((a: any) => a.addon_type === 'tabletop_creator' && a.status === 'active'))));
        // Auto-pick standalone if user has zero BCPs but has credits
        if ((!Array.isArray(bcpData) || bcpData.length === 0) && (creditsData?.credits_remaining ?? 0) > 0) {
          setWithoutBcp(true);
        }
        // Track the post-load error locally so we don't accidentally wipe a
        // verification error with the trailing setError(null).
        let postLoadError: string | null = null;
        const purchaseSessionId = searchParams.get('purchase_session_id');
        if (purchaseSessionId) {
          setWithoutBcp(true);
          // Verify session status instead of trusting URL params.
          try {
            const checkout = await paymentService.getCheckoutSession(purchaseSessionId);
            const paid = Boolean(
              checkout?.status === 'success' &&
              (checkout?.standalone_tabletop || checkout?.session?.payment_status === 'paid')
            );
            setPurchaseSuccess(paid);
            // Backend already activated the purchase if paid — seed credits from the response
            // so the polling effect can short-circuit and the auto-submit fires sooner.
            if (paid && typeof checkout?.credits_remaining === 'number' && checkout.credits_remaining > 0) {
              setCredits(checkout.credits_remaining);
            }
            if (!paid) {
              postLoadError = 'Payment session is not complete yet. Finish checkout before creating the exercise.';
            }
          } catch {
            postLoadError = 'Could not verify payment status. Please refresh and try again.';
          }

          // Restore the in-progress create from before the Stripe redirect (form-flow only)
          try {
            const saved = sessionStorage.getItem(PENDING_CREATE_KEY);
            if (saved) {
              const r = JSON.parse(saved);
              if (r?.form) {
                const savedForm = r.form as Record<string, unknown>;
                if (savedForm.exerciseLead && typeof savedForm.exerciseLead === 'object') {
                  setForm((f) => ({ ...f, ...savedForm, exerciseLead: savedForm.exerciseLead as ExerciseLeadFields }));
                } else if (Array.isArray(savedForm.participants)) {
                  setForm((f) => ({
                    ...f,
                    ...savedForm,
                    exerciseLead: exerciseLeadFromParticipants(savedForm.participants),
                  }));
                } else {
                  setForm((f) => ({ ...f, ...savedForm }));
                }
              }
              if (r?.mode) setMode(isPartnerClient && r.mode === 'manual' ? 'ai' : r.mode);
              if (r?.selectedTemplate) setSelectedTemplate(r.selectedTemplate);
              if (r?.businessContext) setBusinessContext(r.businessContext);
              if (typeof r?.keySystemsRaw === 'string') setKeySystemsRaw(r.keySystemsRaw);
              if (typeof r?.criticalFunctionsRaw === 'string') setCriticalFunctionsRaw(r.criticalFunctionsRaw);
              if (typeof r?.step === 'number') setStep(r.step);
              // Auto-submit only when we're resuming a saved Step 2 payload.
              setPendingAutoSubmit(true);
            }
          } catch {
            // best effort restore — fall back to fresh form
          }
        }
        setError(postLoadError);
      })
      .catch(() => {
        setBcps([]);
        setApiTemplates([]);
        setError('Failed to load BCP plans and templates.');
      })
      .finally(() => setIsLoading(false));
  }, [searchParams, isPartnerClient]);

  useEffect(() => {
    if (isEditing && id) {
      const token = getAccessToken();
      fetch(`${API_BASE_URL}/v1/tabletop-exercises/${id}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      })
        .then((r) => r.ok ? r.json() : null)
        .then((ex) => {
          if (ex) {
            setForm({
              bcpPlan: String(ex.bcp_id ?? ''),
              name: ex.exercise_name ?? '',
              type: ex.exercise_type ?? '',
              description: ex.scenario_description ?? '',
              date: ex.scheduled_date ? ex.scheduled_date.slice(0, 16) : '',
              exerciseLead: exerciseLeadFromParticipants(ex.participants),
            });
            // Standalone (BCP-less) exercise — keep edit form happy
            if (ex.bcp_id == null && ex.business_context) {
              setWithoutBcp(true);
              setBusinessContext({
                business_name: ex.business_context.business_name || '',
                industry: ex.business_context.industry || '',
                business_type: ex.business_context.business_type || '',
                employee_count: ex.business_context.employee_count,
                key_systems: ex.business_context.key_systems || [],
                critical_functions: ex.business_context.critical_functions || [],
              });
              const ks = ex.business_context.key_systems || [];
              setKeySystemsRaw(
                Array.isArray(ks)
                  ? ks.map((s: any) => (typeof s === 'string' ? s : s?.name || '')).filter(Boolean).join(', ')
                  : ''
              );
              const cf = ex.business_context.critical_functions || [];
              setCriticalFunctionsRaw(
                Array.isArray(cf)
                  ? cf.map((f: any) => (typeof f === 'string' ? f : f?.name || '')).filter(Boolean).join(', ')
                  : ''
              );
            }
          }
        })
        .catch(() => {});
    }
  }, [isEditing, id]);

  useEffect(() => {
    setAnimateIn(false);
    const t = setTimeout(() => setAnimateIn(true), 30);
    return () => clearTimeout(t);
  }, [step]);

  useEffect(() => {
    if (isEditing) {
      setEntitlementLoaded(true);
      return;
    }
    if (!creditsLoaded) return;
    const token = getAccessToken();
    if (!token) {
      setEntitlementLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/v1/tabletop-exercises/entitlement/`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const data = r.ok ? await r.json() : { allowed: false };
        if (!cancelled) {
          setEntitlementAllowed(Boolean(data.allowed));
          const access = data.access || data.source;
          if (access?.kind === 'partner_credit') {
            setPartnerCreditName(access.insurer_name || 'Insurance partner');
            const tierName = access.partner_token_name || (access.partner_token_tier ? tokenTitles[access.partner_token_tier] : null);
            setPartnerTokenTierName(tierName);
            setWithoutBcp(true);
          } else {
            setPartnerCreditName(null);
            setPartnerTokenTierName(null);
          }
          if (typeof data.credits_remaining === 'number') {
            setCredits(data.credits_remaining);
            if (data.credits_remaining > 0 && bcps.length === 0) {
              setWithoutBcp(true);
            }
          }
        }
      } catch {
        if (!cancelled) setEntitlementAllowed(false);
      } finally {
        if (!cancelled) setEntitlementLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bcps.length, credits, creditsLoaded, isEditing, tokenTitles]);

  useEffect(() => {
    if (isPartnerClient) {
      setWithoutBcp(true);
      if (mode === 'manual') {
        setMode('ai');
      }
    }
  }, [isPartnerClient, mode]);

  // Poll credits after returning from Stripe — webhook may take a moment to land.
  // Runs in BOTH flows (form-flow with saved state, modal-flow without) so the submit
  // guard sees a non-zero balance before letting the user click again.
  useEffect(() => {
    if (!purchaseSuccess) return;
    if (credits > 0) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await paymentService.getStandaloneTabletopCredits();
        if (!cancelled && (data.credits_remaining ?? 0) > 0) {
          setCredits(data.credits_remaining);
          setCreditsPerPack(data.credits_per_pack ?? 1);
          setCreditsLoaded(true);
          return;
        }
      } catch {
        // swallow — keep polling
      }
      if (attempts < 20 && !cancelled) {
        setTimeout(tick, 2000);
      } else if (!cancelled) {
        setError('Payment received, but credits are still syncing. Please refresh in a moment.');
        setPendingAutoSubmit(false);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [purchaseSuccess, credits]);

  // Once credits land after a Stripe round-trip, auto-fire submit so the user doesn't have to click again
  useEffect(() => {
    if (!pendingAutoSubmit) return;
    if (credits <= 0) return;
    if (isSubmitting) return;
    setPendingAutoSubmit(false);
    sessionStorage.removeItem(PENDING_CREATE_KEY);
    // Defer one tick so React commits credits state before submit reads it
    const t = setTimeout(() => handleSubmit(), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credits, pendingAutoSubmit, isSubmitting]);

  const selectTemplate = (tpl: DisplayTemplate) => {
    setSelectedTemplate(tpl);
    const scenarioDesc = tpl.scenarioTemplate ?? tpl.desc;
    const nameSuffix = tpl.isApi ? ` - ${new Date().toLocaleDateString()}` : ' Exercise';
    setForm((f) => ({ ...f, name: tpl.name + nameSuffix, type: tpl.type, description: scenarioDesc }));
  };

  const updateExerciseLead = (field: keyof ExerciseLeadFields, value: string) => {
    setForm((f) => ({
      ...f,
      exerciseLead: { ...f.exerciseLead, [field]: value },
    }));
  };

  const hasTabletopAccess = hasSubAccess || credits > 0 || entitlementAllowed;
  const isStaffUiBypass = Boolean(
    user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN')
  );
  const canPickScenario =
    isEditing ||
    isStaffUiBypass ||
    (creditsLoaded && credits > 0) ||
    entitlementAllowed;

  const scenarioModeOptions = isPartnerClient
    ? [{ key: 'ai' as const, icon: '✨', title: 'Activate Your Scenario', desc: "Pick a template and we'll generate a full scenario for you." }]
    : [
        { key: 'ai' as const, icon: '✨', title: 'Activate Your Scenario', desc: "Pick a template and we'll generate a full scenario for you." },
        { key: 'manual' as const, icon: '✏️', title: 'Build Your Own', desc: 'Write a custom scenario from scratch with full control.' },
      ];

  const isGeneratingScenario =
    isSubmitting && !isEditing && step === 2 && mode === 'ai' && selectedTemplate;

  const submitButtonLabel = () => {
    if (!isSubmitting) {
      if (isEditing) return step === 1 ? 'Update Exercise' : 'Continue →';
      if (step === 2) {
        if (withoutBcp && !hasTabletopAccess && !isPartnerClient && credits <= 0) {
          return purchaseSuccess ? 'Confirming payment…' : 'Pay $500 & Create →';
        }
        return 'Create Exercise';
      }
      return 'Continue →';
    }
    if (isEditing && step === 1) return 'Updating…';
    if (!isEditing && step === 2) {
      if (withoutBcp && !hasTabletopAccess && !isPartnerClient && credits <= 0 && !purchaseSuccess) {
        return 'Redirecting to payment…';
      }
      if (mode === 'ai') return 'Generating scenario…';
      return 'Creating exercise…';
    }
    return 'Saving…';
  };

  const pickMode = (m: ScenarioMode) => {
    if (!canPickScenario) return;
    if (isPartnerClient && m === 'manual') return;
    setMode(m);
  };

  const pickTemplateWrapper = (tpl: DisplayTemplate) => {
    if (!canPickScenario) return;
    selectTemplate(tpl);
  };

  const sourceOk = (): boolean => {
    if (withoutBcp) return Boolean(businessContext.business_name && businessContext.business_name.trim().length > 0);
    return form.bcpPlan !== '';
  };

  const canProceed = (): boolean => {
    if (isEditing) {
      if (step === 0) {
        // Edit only updates the editable fields below — source (BCP / standalone) is fixed.
        return !!form.name && !!form.type && !!form.description;
      }
      return true;
    }
    if (step === 0) return canPickScenario && mode !== null;
    if (step === 1) {
      if (mode === 'ai') return selectedTemplate !== null && sourceOk();
      return sourceOk() && !!form.name && !!form.type && !!form.description;
    }
    return true;
  };

  const handleSubmit = async () => {
    const token = getAccessToken();
    if (!token) return;

    if (isEditing && step === 1 && id) {
      setIsSubmitting(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/v1/tabletop-exercises/${id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            exercise_name: form.name,
            exercise_type: form.type,
            scenario_description: form.description,
            scheduled_date: form.date || null,
            participants: buildExerciseLeadPayload(form.exerciseLead),
          }),
        });
        if (res.ok) {
          const ex = await res.json();
          navigate(`/tabletop-exercises/${ex.id}`);
        } else {
          const err = await res.json().catch(() => ({}));
          const msg = Array.isArray(err.detail) ? err.detail.map((e: { msg?: string }) => e.msg || JSON.stringify(e)).join(', ') : err.detail;
          setError(msg || 'Update failed');
        }
      } catch {
        setError('Update failed');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!isEditing && step === 2) {
      setIsSubmitting(true);
      setError(null);

      // Standalone path: figure out whether to charge or use existing credits.
      if (withoutBcp && !hasTabletopAccess) {
        if (isPartnerClient) {
          setError('Your insurer has not provisioned a tabletop credit yet. Contact them to request access.');
          setIsSubmitting(false);
          return;
        }
        // Wait until we know the credit balance — without this we'd risk redirecting
        // a user who actually has credits to a fresh checkout (double charge).
        if (!creditsLoaded || !entitlementLoaded) {
          setError('Loading your credit balance — try again in a moment.');
          setIsSubmitting(false);
          return;
        }

        if (credits <= 0) {
          // If we just came back from a payment (purchase_session_id in URL), the webhook
          // hasn't credited the account yet. Do NOT redirect to a new checkout — that would
          // double-charge. Wait for the polling effect to populate credits, then they can retry.
          if (purchaseSuccess) {
            setError('Payment received — finalizing your credits. Please wait a few seconds and retry.');
            setIsSubmitting(false);
            return;
          }

          // Legitimate fresh purchase: save form state, redirect to Stripe.
          try {
            sessionStorage.setItem(
              PENDING_CREATE_KEY,
              JSON.stringify({
                form,
                mode,
                selectedTemplate,
                businessContext,
                keySystemsRaw,
                criticalFunctionsRaw,
                step: 2,
                withoutBcp: true,
              })
            );
            const data = await paymentService.createStandaloneTabletopCheckoutSession({ returnTo: '/tabletop-exercises/new' });
            window.location.href = data.checkout_url;
          } catch (err: any) {
            // Server may refuse (409) because user actually has unused credits we didn't know about.
            // Refetch to self-heal — if credits show up, the user can re-click and submit normally.
            try {
              const fresh = await paymentService.getStandaloneTabletopCredits();
              if ((fresh?.credits_remaining ?? 0) > 0) {
                setCredits(fresh.credits_remaining);
                setCreditsPerPack(fresh?.credits_per_pack ?? 1);
                setCreditsLoaded(true);
                setError('We found unused credits on your account. Click Create Exercise again to use one.');
                setIsSubmitting(false);
                return;
              }
            } catch {
              // ignore — fall through to generic error
            }
            setError(err?.message || 'Could not start checkout');
            setIsSubmitting(false);
          }
          return;
        }
      }

      const ctx: BusinessContext | null = withoutBcp
        ? {
            business_name: businessContext.business_name.trim(),
            industry: businessContext.industry || undefined,
            business_type: businessContext.business_type || undefined,
            employee_count: businessContext.employee_count,
            key_systems: keySystemsRaw
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
            critical_functions: criticalFunctionsRaw
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          }
        : null;

      // AI flow on standalone uses generate-by-category which produces full SOW scenario
      if (mode === 'ai' && selectedTemplate) {
        const category = SUBCATEGORY_TO_CATEGORY[selectedTemplate.type];
        if (!category) {
          setError(`Selected template type "${selectedTemplate.type}" is not supported by the AI generator yet.`);
          setIsSubmitting(false);
          return;
        }
        try {
          const ex = await generateTabletopByCategory({
            bcp_id: withoutBcp ? null : parseInt(form.bcpPlan, 10),
            business_context: ctx,
            category,
            subcategory: selectedTemplate.type as ScenarioSubcategory,
            exercise_name: form.name,
            scheduled_date: form.date || undefined,
            participants: buildExerciseLeadPayload(form.exerciseLead),
          });
          navigate(`/tabletop-exercises/${ex.id}`);
        } catch (err: any) {
          const detail = err?.response?.data?.detail || err?.message || 'Create failed';
          setError(typeof detail === 'string' ? detail : 'Create failed');
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      // Manual path keeps the legacy POST endpoint (now schema-aware of business_context)
      try {
        const res = await fetch(`${API_BASE_URL}/v1/tabletop-exercises/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bcp_id: withoutBcp ? null : parseInt(form.bcpPlan, 10),
            business_context: ctx,
            exercise_name: form.name,
            exercise_type: form.type,
            scenario_description: form.description,
            scheduled_date: form.date || null,
            participants: buildExerciseLeadPayload(form.exerciseLead),
            use_ai: false,
          }),
        });
        if (res.ok) {
          const ex = await res.json();
          navigate(`/tabletop-exercises/${ex.id}`);
        } else {
          const err = await res.json().catch(() => ({}));
          const msg = Array.isArray(err.detail) ? err.detail.map((e: { msg?: string }) => e.msg || JSON.stringify(e)).join(', ') : err.detail;
          setError(msg || 'Create failed');
        }
      } catch {
        setError('Create failed');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setStep(step + 1);
  };

  const handleBuyCredits = async () => {
    try {
      const data = await paymentService.createStandaloneTabletopCheckoutSession({ returnTo: '/tabletop-exercises/new' });
      window.location.href = data.checkout_url;
    } catch (err: any) {
      setError(err?.message || 'Could not start checkout');
    }
  };

  const inputClassName = 'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors';
  const readOnlyFieldClassName = 'bg-gray-100 text-gray-600 cursor-not-allowed';
  const isExerciseTypeLocked = isEditing || mode === 'ai';
  const labelClassName = 'block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2';

  if (isLoading || (!isEditing && !entitlementLoaded)) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[40vh]">
        <div className="animate-pulse space-y-4 text-center">
          <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto" />
          <div className="h-64 bg-gray-200 rounded max-w-md mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex justify-center px-5 py-10">
      <div className="w-full max-w-[720px]">
        <div className="mb-2">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Edit Exercise' : 'Create Exercise'}
          </h1>
          <p className="text-gray-600 text-sm mt-2 mb-8 leading-relaxed">
            {isEditing ? 'Update your table top exercise details.' : "Design a business continuity exercise to test your team's readiness."}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
            {error}
          </div>
        )}

        {isGeneratingScenario && (
          <div className="mb-4 p-4 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-3">
            <div
              className="mt-0.5 w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0"
              role="status"
              aria-label="Generating scenario"
            />
            <div>
              <p className="font-semibold text-blue-900 text-sm">Generating your scenario</p>
              <p className="text-blue-700 text-sm mt-0.5">
                This usually takes 30–60 seconds. Please keep this page open.
              </p>
            </div>
          </div>
        )}

        {!isEditing && !error && purchaseSuccess && credits > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm flex items-center justify-between">
            <span>
              <span className="font-semibold">Payment received.</span> You have {credits} tabletop credit{credits === 1 ? '' : 's'} available — finish the form below to use one.
            </span>
          </div>
        )}

        {!isEditing && !error && purchaseSuccess && credits === 0 && (
          <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm">
            Payment confirmed — finalizing your credits. This usually takes a few seconds.
          </div>
        )}

        {!isEditing && !error && !purchaseSuccess && !isPartnerClient && bcps.length === 0 && !withoutBcp && (
          <div className="mb-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
            No BCP plans found. You can either create a BCP first, or run a <span className="font-semibold">standalone tabletop ($500 = 1 exercise)</span> — toggle "No BCP — standalone" in the configure step.
          </div>
        )}

        <StepIndicator steps={steps} current={currentStep} />

        {partnerCreditName && credits > 0 && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
            Covered by {partnerCreditName}
            {partnerTokenTierName ? ` (${partnerTokenTierName} token)` : ''} — {credits} credit{credits === 1 ? '' : 's'} remaining
          </div>
        )}

        <div
          ref={contentRef}
          className={`transition-all duration-300 ease-out ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
        >
          {/* STEP 0: Choose Mode (create only) */}
          {!isEditing && step === 0 && !canPickScenario && (
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-8 text-center">
              <h2 className="text-lg font-bold text-gray-900 mb-3">Tabletop access required</h2>
              <p className="text-sm text-gray-700 mb-6 leading-relaxed">
                Choose Premium, add Tabletop Creator to your subscription, or purchase a one-time $500 tabletop
                credit to generate or configure a scenario.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                {!partnerCreditName && (
                  <button
                    type="button"
                    onClick={handleBuyCredits}
                    className="px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700"
                  >
                    Buy $500 tabletop credit
                  </button>
                )}
                <Link
                  to="/tabletop-exercises"
                  className="text-sm font-semibold text-blue-600 hover:text-blue-800"
                >
                  Back to dashboard
                </Link>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                If you already paid in another tab, refresh this page — credits can take a few seconds to sync.
              </p>
            </div>
          )}

          {!isEditing && step === 0 && canPickScenario && (
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-5">
                How would you like to build your scenario?
              </h2>
              <div className={`grid gap-4 mb-8 ${scenarioModeOptions.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 max-w-md'}`}>
                {scenarioModeOptions.map((m) => (
                  <div
                    key={m.key}
                    onClick={() => pickMode(m.key)}
                    className={`p-6 rounded-xl border-2 cursor-pointer transition-all relative overflow-hidden hover:shadow-md ${
                      mode === m.key ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    {mode === m.key && (
                      <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs font-bold text-blue-600">
                        ✓
                      </div>
                    )}
                    <div className="text-2xl mb-3">{m.icon}</div>
                    <div className="text-base font-bold mb-1.5">{m.title}</div>
                    <div className={`text-sm leading-relaxed ${mode === m.key ? 'text-blue-100' : 'text-gray-600'}`}>{m.desc}</div>
                  </div>
                ))}
              </div>

              {mode === 'ai' && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Choose a template</h3>
                  <div className="flex flex-col gap-2.5">
                    {templateOptions.map((tpl) => (
                      <div
                        key={tpl.id}
                        onClick={() => pickTemplateWrapper(tpl)}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                          selectedTemplate?.id === tpl.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="flex-1 min-w-0 pr-4">
                          <div className="text-sm font-bold text-gray-900">{tpl.name}</div>
                          <div className="text-xs text-gray-600 mt-0.5">{tpl.desc}</div>
                        </div>
                        <div className="flex gap-2 items-center flex-shrink-0">
                          <Badge variant={tpl.level}>{tpl.level}</Badge>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{tpl.duration} min</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 1: Configure (or step 0 when editing) */}
          {(isEditing ? step === 0 : step === 1) && (
            <div>
              {selectedTemplate && mode === 'ai' && !isEditing && (
                <div className="p-3.5 rounded-xl bg-blue-50 border border-blue-200 mb-7">
                  <div>
                    <div className="text-sm font-bold text-gray-900">{selectedTemplate.name}</div>
                    <div className="text-xs text-gray-600">Template selected — AI will generate the full scenario</div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-6">
                {!isPartnerClient && (
                <>
                {/* Choose your plan: existing BCP, or "No plan" -> standalone $500 path */}
                <div>
                  <label className={labelClassName}>Choose your plan *</label>
                  <select
                    value={withoutBcp ? NO_PLAN_OPTION : form.bcpPlan}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === NO_PLAN_OPTION) {
                        setWithoutBcp(true);
                        setForm((f) => ({ ...f, bcpPlan: '' }));
                      } else {
                        setWithoutBcp(false);
                        setForm((f) => ({ ...f, bcpPlan: v }));
                      }
                    }}
                    className={`${inputClassName} cursor-pointer`}
                  >
                    <option value="">Choose a plan...</option>
                    {bcps.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.business_name}{p.industry ? ` (${p.industry})` : ''}
                      </option>
                    ))}
                    <option value={NO_PLAN_OPTION}>
                      No plan — standalone exercise ($500 charged at create)
                    </option>
                  </select>
                </div>

                {/* Standalone access banner */}
                {withoutBcp && (
                  <div className={`p-3 rounded-lg border text-sm ${
                    purchaseSuccess && credits === 0
                      ? 'bg-blue-50 border-blue-200 text-blue-900'
                      : purchaseSuccess
                      ? 'bg-green-50 border-green-200 text-green-900'
                      : hasSubAccess
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-900'
                      : credits > 0
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-900'
                      : 'bg-amber-50 border-amber-200 text-amber-900'
                  }`}>
                    {hasSubAccess ? (
                      <p>Standalone exercises are included with your current plan/add-on.</p>
                    ) : purchaseSuccess && credits === 0 ? (
                      <p>
                        <span className="font-semibold">Payment received.</span> Confirming your credits with our payment processor — this usually takes a few seconds. You won't be charged again.
                      </p>
                    ) : credits > 0 ? (
                      <p>
                        {purchaseSuccess && <span className="font-semibold">Payment confirmed. </span>}
                        You have <span className="font-semibold">{credits}</span> tabletop credit{credits === 1 ? '' : 's'} available. This exercise will use 1 credit.
                      </p>
                    ) : (
                      <p>
                        $500 will be charged when you click <span className="font-semibold">Create Exercise</span>.
                        {creditsPerPack <= 1 ? (
                          <> That covers <span className="font-semibold">one</span> tabletop exercise.</>
                        ) : (
                          <>
                            {' '}
                            That covers <span className="font-semibold">{creditsPerPack}</span> tabletop exercises.
                          </>
                        )}
                      </p>
                    )}
                  </div>
                )}
                </>
                )}

                {!withoutBcp ? null : (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Tell us about your business</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <label className={labelClassName}>Business Name *</label>
                        <input
                          type="text"
                          value={businessContext.business_name}
                          onChange={(e) => setBusinessContext((c) => ({ ...c, business_name: e.target.value }))}
                          className={inputClassName}
                          placeholder="e.g. Apex Global Technologies, Inc."
                        />
                      </div>
                      <div>
                        <label className={labelClassName}>Industry</label>
                        <input
                          type="text"
                          value={businessContext.industry || ''}
                          onChange={(e) => setBusinessContext((c) => ({ ...c, industry: e.target.value }))}
                          className={inputClassName}
                          placeholder="Manufacturing, Healthcare, SaaS…"
                        />
                      </div>
                      <div>
                        <label className={labelClassName}>Business Type</label>
                        <input
                          type="text"
                          value={businessContext.business_type || ''}
                          onChange={(e) => setBusinessContext((c) => ({ ...c, business_type: e.target.value }))}
                          className={inputClassName}
                          placeholder="LLC, Corp, Non-profit…"
                        />
                      </div>
                      <div>
                        <label className={labelClassName}>Employee Count</label>
                        <input
                          type="number"
                          min={0}
                          value={businessContext.employee_count ?? ''}
                          onChange={(e) =>
                            setBusinessContext((c) => ({
                              ...c,
                              employee_count: e.target.value ? parseInt(e.target.value, 10) : undefined,
                            }))
                          }
                          className={inputClassName}
                          placeholder="50"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelClassName}>Key Systems (comma-separated)</label>
                        <input
                          type="text"
                          value={keySystemsRaw}
                          onChange={(e) => setKeySystemsRaw(e.target.value)}
                          className={inputClassName}
                          placeholder="ERP, CRM, Email, Active Directory"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelClassName}>Critical Functions (comma-separated)</label>
                        <input
                          type="text"
                          value={criticalFunctionsRaw}
                          onChange={(e) => setCriticalFunctionsRaw(e.target.value)}
                          className={inputClassName}
                          placeholder="Order fulfillment, customer support, payroll"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className={labelClassName}>Exercise Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. Q1 Ransomware Tabletop"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    readOnly={isEditing}
                    className={`${inputClassName} ${isEditing ? readOnlyFieldClassName : ''}`}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClassName}>Exercise Type *</label>
                    <select
                      value={form.type}
                      onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                      disabled={isExerciseTypeLocked}
                      className={`${inputClassName} ${isExerciseTypeLocked ? readOnlyFieldClassName : 'cursor-pointer'}`}
                    >
                      <option value="">Select type...</option>
                      {exerciseTypeOptions.map((t) => {
                        const match = availableExerciseTypes.find((et) => et.key === t);
                        const label = match
                          ? `${match.icon ? `${match.icon} ` : ''}${match.name}`
                          : t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                        return (
                          <option key={t} value={t}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className={labelClassName}>Scheduled Date</label>
                    <input
                      type="datetime-local"
                      value={form.date}
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                      className={inputClassName}
                    />
                  </div>
                </div>

                {(mode === 'manual' || isEditing) && (
                  <div>
                    <div className="flex justify-between items-baseline">
                      <label className={labelClassName}>Scenario Description *</label>
                      <span className="text-xs text-gray-400">{form.description.length} / 2000</span>
                    </div>
                    <textarea
                      placeholder="Describe the scenario your team will respond to during this exercise..."
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, 2000) }))}
                      rows={5}
                      className={`${inputClassName} resize-y leading-relaxed`}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 2: Exercise Lead (or step 1 when editing) */}
          {(isEditing ? step === 1 : step === 2) && (
            <div>
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-900">Exercise Lead</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Optional — who is facilitating this exercise. Participants can be recorded on the downloadable worksheet.
                </p>
              </div>

              <div className="p-5 rounded-xl bg-white border border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    placeholder="Full name"
                    value={form.exerciseLead.name}
                    onChange={(e) => updateExerciseLead('name', e.target.value)}
                    className={`${inputClassName} py-2.5 text-sm`}
                  />
                  <select
                    value={form.exerciseLead.role}
                    onChange={(e) => updateExerciseLead('role', e.target.value)}
                    className={`${inputClassName} py-2.5 text-sm cursor-pointer`}
                  >
                    <option value="">Select role...</option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <input
                    placeholder="Email"
                    value={form.exerciseLead.email}
                    onChange={(e) => updateExerciseLead('email', e.target.value)}
                    className={`${inputClassName} py-2.5 text-sm`}
                  />
                  <input
                    placeholder="Phone (optional)"
                    value={form.exerciseLead.phone}
                    onChange={(e) => updateExerciseLead('phone', e.target.value)}
                    className={`${inputClassName} py-2.5 text-sm`}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => (step > 0 ? setStep(step - 1) : navigate('/tabletop-exercises'))}
            disabled={isSubmitting}
            className={`py-3 px-6 rounded-lg border-0 font-semibold text-sm transition-colors ${
              isSubmitting
                ? 'text-gray-300 cursor-not-allowed'
                : step > 0
                ? 'bg-transparent text-gray-600 hover:text-gray-900 cursor-pointer'
                : 'text-gray-400 cursor-default'
            }`}
          >
            {step === 0 && !isEditing ? 'Cancel' : '← Back'}
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canProceed() || isSubmitting}
            aria-busy={isSubmitting}
            className={`py-3 px-8 rounded-lg font-bold text-sm transition-all inline-flex items-center justify-center gap-2 min-w-[11rem] ${
              isSubmitting
                ? 'bg-blue-600 text-white cursor-wait opacity-90 shadow-md'
                : canProceed()
                ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer shadow-md hover:shadow-lg'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting && (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
            )}
            {submitButtonLabel()}
          </button>
        </div>

        <p className="mt-8 text-xs text-gray-500 leading-relaxed">
          {TABLETOP_ASSESSMENT_DISCLAIMER}
        </p>
      </div>
    </div>
  );
};

export default CreateExerciseForm;
